import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import { Alert, Box, Button, Container, GridLegacy as Grid, Typography, Stack, Paper, Divider } from '@mui/material';
import { OverviewActiveOrders } from '../dashboard/OverviewActiveOrders';
import { OverviewLatestOrders } from '../dashboard/OverviewLatestOrders';
import { OverviewChannelDailyChart } from '../dashboard/OverviewChannelDailyChart';
import { ReportsIcon } from '../icons/ReportsIcon';
import { useInterval } from '../../hooks/useInterval';
import { OverviewGenericCard } from '../dashboard/OverviewGenericCard';
import { buildSalesPerformanceCharts } from '../dashboard/salesPerformanceCharts';
import NextLink from 'next/link';
import toast from 'react-hot-toast';
import { useIntegrationsData } from '../../contexts/IntegrationsContext';
import { getDashboardAlerts } from '@/endpoints/dashboard/getDashboardAlerts';
import { getDashboardMetrics } from '@/endpoints/dashboard/getDashboardMetrics';
import { getPartiallyRoutedOrdersCount } from '@/endpoints/dashboard/getPartiallyRoutedOrdersCount';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';
import { requestScopeFor } from '@/session/scopedTransport';
import { acquireOperationContext, operationRequirement } from '@/session/operation';
import type { DashboardAlert, DashboardDisplayKey, DashboardMetrics } from '../../types/dashboard';
import { useField } from '../../hooks/useField';
import { utils } from '../../utils';
import { useKeycloak } from '@react-keycloak/ssr';
import { useOrganizationData } from '@/contexts/OrganizationContext';
import { useOperationRuns } from '@/session/useOperationRuns';
import { useTranslation } from 'react-i18next';
import { OrgTimezoneNotice } from '../misc/OrgTimezoneNotice';
import { OrderIssuesAlert } from '../order-issues/OrderIssuesAlert';
import { DashboardActionAlert } from '../dashboard/DashboardActionAlert';

type DashboardMetricSnapshot = {
  result: DashboardMetrics;
  timezone: string;
};

type AlertType = 'error' | 'warning' | 'info' | 'success';

export interface OverviewLayoutProps {}

export const OverviewLayout: React.FC<OverviewLayoutProps> = () => {
  const { t } = useTranslation();
  const { keycloak, initialized } = useKeycloak();
  const organization = useCommittedOrganization();
  const { timezone } = useOrganizationData();
  const dashboardData = useField<DashboardMetricSnapshot>({ result: {}, timezone });
  const dashboardAlerts = useField<DashboardAlert[]>([]);
  const loading = useField<boolean>(true);
  const partiallyRoutedOrdersCount = useField<number>(0);
  const [isAlertDismissed, setIsAlertDismissed] = useState<boolean>(false);
  const alert_types: AlertType[] = ['error', 'warning', 'info', 'success'];
  const { sChannels, busySC, hasFetched, refreshIntegrationsData } = useIntegrationsData();
  const operationRuns = useOperationRuns();
  const dashboardSettersRef = useRef({
    setAlerts: dashboardAlerts.setValue,
    setData: dashboardData.setValue,
    setLoading: loading.setValue,
    setPartiallyRoutedCount: partiallyRoutedOrdersCount.setValue,
  });
  const dashboardSeries = useMemo(() => buildSalesPerformanceCharts(dashboardData.value.result, dashboardData.value.timezone), [dashboardData.value]);

  const dd = function (name: DashboardDisplayKey, isCurrency: boolean = false, formatNumber: boolean = false): string {
    const d = dashboardData.value.result[name];
    let retval = '0';
    if (d != null) {
      retval = String(d);
    }
    let formatted = isCurrency ? parseFloat(retval).toFixed(0) : retval;
    if (formatNumber) {
      const num = parseFloat(formatted);
      if (!isNaN(num)) {
        formatted = num.toLocaleString('en-US');
      }
    }
    return formatted;
  };

  const loadDashboard = useCallback(async (): Promise<void> => {
    const operation = acquireOperationContext(organization);
    if (!operation.isScopeCurrent()) return;
    const run = operationRuns.begin('overview-dashboard', operation);
    const setters = dashboardSettersRef.current;
    run.publish(() => setters.setLoading(true));
    const scope = requestScopeFor(organization);
    try {
      const [alertsResponse, partialResponse, metricsResponse] = await Promise.all([
        getDashboardAlerts(scope, {
          signal: operation.scopeSignal,
          operationRequirement: operationRequirement(operation, 'scope'),
        }),
        getPartiallyRoutedOrdersCount(scope, {
          signal: operation.scopeSignal,
          operationRequirement: operationRequirement(operation, 'scope'),
        }),
        getDashboardMetrics(
          scope,
          { timezone },
          {
            signal: operation.scopeSignal,
            operationRequirement: operationRequirement(operation, 'scope'),
          }
        ),
      ]);

      run.publish(() => {
        if (alertsResponse.isError) {
          if (!utils.error.isCanceledResponse(alertsResponse)) toast.error(alertsResponse.errors.error ?? t('Failed to load dashboard alerts'));
        } else {
          setters.setAlerts(alertsResponse.result);
        }

        if (partialResponse.isError) {
          if (!utils.error.isCanceledResponse(partialResponse)) {
            toast.error(partialResponse.errors.error ?? t('Failed to load partially routed orders count'));
          }
        } else {
          setters.setPartiallyRoutedCount(partialResponse.result.count);
        }

        if (metricsResponse.isError) {
          if (!utils.error.isCanceledResponse(metricsResponse)) toast.error(metricsResponse.errors.error ?? t('Failed to load dashboard data'));
          setters.setData({ result: {}, timezone });
        } else {
          setters.setData({ result: metricsResponse.result, timezone });
        }
      });
    } finally {
      run.settle(() => setters.setLoading(false));
    }
  }, [operationRuns, organization, t, timezone]);

  useEffect(() => {
    if (!hasFetched) {
      refreshIntegrationsData();
    }
  }, [hasFetched, refreshIntegrationsData]);

  useInterval(() => {
    void loadDashboard();
  }, 60 * 1000);

  useEffect(() => {
    if (initialized && keycloak?.authenticated) void loadDashboard();
  }, [keycloak?.authenticated, initialized, loadDashboard]);

  return (
    <>
      <Head>
        <title>Dashboard: Overview</title>
      </Head>
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          py: { xs: 3, md: 4 },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Container maxWidth='xl'>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', md: 'center' }} spacing={3}>
              <Box>
                <Typography
                  variant='h5'
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 0.5,
                  }}
                >
                  {utils.stringUtils.titleCase(organization).replaceAll('_', ' ')} Dashboard
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 400,
                  }}
                >
                  Monitor your business performance and key metrics
                </Typography>
              </Box>

              <Stack direction='row' spacing={3} alignItems='center'>
                <Stack direction='row' spacing={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant='overline'
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        lineHeight: 1,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Orders Today
                    </Typography>
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        lineHeight: 1,
                      }}
                    >
                      {loading.value ? '...' : dd('order_count_today', false, true)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant='overline'
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        lineHeight: 1,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Total Orders
                    </Typography>
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        lineHeight: 1,
                      }}
                    >
                      {loading.value ? '...' : dd('order_count_total', false, true)}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  component={NextLink}
                  href={`/${organization}/reports`}
                  startIcon={<ReportsIcon />}
                  variant='outlined'
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  View Reports
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <OrderIssuesAlert />

          {partiallyRoutedOrdersCount.value > 0 && !isAlertDismissed && (
            <DashboardActionAlert
              actionHref={`/${organization}/orders/?tab=all&status=pending%2Cpartially_routed`}
              actionLabel='Fix Orders'
              onClose={() => setIsAlertDismissed(true)}
            >
              <strong>Orders Need Attention:</strong> There are currently {partiallyRoutedOrdersCount.value} orders for skus that do not exist in our
              system. Add missing skus or review aliases.
            </DashboardActionAlert>
          )}

          {dashboardAlerts.value && dashboardAlerts.value.length > 0 && (
            <Stack spacing={2} sx={{ mb: 3 }}>
              {dashboardAlerts.value.map((alert: DashboardAlert, index: number) => (
                <React.Fragment key={index}>
                  {alert.global === true && (
                    <Alert
                      severity={alert_types.includes(alert.type) ? alert.type : 'info'}
                      sx={{
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                          fontWeight: 500,
                        },
                      }}
                    >
                      {alert.message}
                    </Alert>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          )}

          <OrgTimezoneNotice />

          <Grid container spacing={3}>
            <Grid item xs={12} lg={8} sx={{ order: { xs: 2, lg: 1 } }}>
              <Stack spacing={3}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <OverviewChannelDailyChart metric='sales' />
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <OverviewChannelDailyChart metric='orders' />
                </Paper>

                {timezone && (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <OverviewLatestOrders timezone={timezone} sChannels={sChannels || []} sChannelsLoading={busySC} />
                  </Paper>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4} sx={{ order: { xs: 1, lg: 2 } }}>
              <Stack spacing={3}>
                {timezone && (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <OverviewActiveOrders timezone={timezone} sChannels={sChannels || []} sChannelsLoading={busySC} />
                  </Paper>
                )}

                {timezone && (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ p: 3, pb: 2 }}>
                      <Typography
                        variant='h6'
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 2,
                        }}
                      >
                        Sales Performance
                      </Typography>
                    </Box>

                    <Stack spacing={0} divider={<Divider />}>
                      <Box sx={{ p: 3, pt: 0 }}>
                        <OverviewGenericCard
                          loading={loading.value}
                          title={'Total Sales Today'}
                          isPrice={true}
                          value={dd('sales_today', true, false)}
                          data={dashboardSeries.sales_today_by_hour}
                        />
                      </Box>

                      <Box sx={{ p: 3 }}>
                        <OverviewGenericCard
                          loading={loading.value}
                          title={'Total Sales WTD'}
                          isPrice={true}
                          value={dd('sales_wtd', true, false)}
                          data={dashboardSeries.sales_wtd_by_day}
                        />
                      </Box>

                      <Box sx={{ p: 3 }}>
                        <OverviewGenericCard
                          loading={loading.value}
                          title={'Total Sales MTD'}
                          isPrice={true}
                          value={dd('sales_mtd', true, false)}
                          data={dashboardSeries.sales_mtd_by_day}
                        />
                      </Box>

                      <Box sx={{ p: 3 }}>
                        <OverviewGenericCard
                          loading={loading.value}
                          title={'Total Sales YTD'}
                          isPrice={true}
                          value={dd('sales_ytd', true, false)}
                          data={dashboardSeries.sales_ytd_by_month}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};
